"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

export interface ChapterDefinition {
  id: string;
  label: string;
}

interface ChapterNavProps {
  chapters: ChapterDefinition[];
  /**
   * Layout direction. Vertical is the desktop sidebar variant; horizontal is
   * the mobile pinned scroller (auto-scrolls the active item into view).
   */
  orientation?: "vertical" | "horizontal";
  className?: string;
}

/** Fallback when the page nav can't be measured (e.g. before mount). Matches
 *  action-bar 36 + nav 60 + a small buffer. */
const DEFAULT_HEADER_OFFSET = 108;

/** Find the visionaries page nav so we can use its bottom edge as the
 *  active-section threshold. Falls back to a constant when not found. */
function getHeaderOffset(): number {
  if (typeof document === "undefined") return DEFAULT_HEADER_OFFSET;
  const nav = document.querySelector<HTMLElement>("[data-vs-page-nav]");
  if (!nav) return DEFAULT_HEADER_OFFSET;
  const rect = nav.getBoundingClientRect();
  // bottom of the nav (in viewport coords) + a 12px buffer so a section
  // counts as "active" once its top reaches just under the nav.
  return rect.bottom + 12;
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function ChapterNav({
  chapters,
  orientation = "vertical",
  className,
}: ChapterNavProps) {
  const [activeId, setActiveId] = React.useState<string | null>(
    chapters[0]?.id ?? null
  );
  const fillRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
  const itemRefs = React.useRef<Record<string, HTMLAnchorElement | null>>({});
  const navRef = React.useRef<HTMLElement>(null);
  const frameRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const compute = () => {
      frameRef.current = null;
      const viewportTop = window.scrollY;
      const headerOffset = getHeaderOffset();
      const headerLine = viewportTop + headerOffset;
      let nextActive = chapters[0]?.id ?? null;

      // The last chapter's section can sit close to (or flush against) the
      // bottom of the document, so `headerLine` may never reach as far as
      // its true bottom edge — the page runs out of scroll room first.
      // Clamp each section's fill denominator to whatever portion of it is
      // actually reachable (`maxHeaderLine`, the header line's value at max
      // scroll) so the bar rescales smoothly across that reachable span
      // instead of stalling partway and then snapping to 100% at the last
      // pixel of scroll.
      const maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight
      );
      const maxHeaderLine = maxScroll + headerOffset;

      chapters.forEach((chapter) => {
        const el = document.getElementById(chapter.id);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const top = rect.top + viewportTop;
        const height = rect.height || 1;
        const reachableHeight = Math.min(
          height,
          Math.max(1, maxHeaderLine - top)
        );

        const fill = Math.max(
          0,
          Math.min(1, (headerLine - top) / reachableHeight)
        );

        const bar = fillRefs.current[chapter.id];
        if (bar) {
          // Same horizontal scaleX progression for both orientations — just
          // applied to a list-row underline (vertical) or a tab pill
          // underline (horizontal).
          bar.style.transform = `scaleX(${fill})`;
        }

        if (top <= headerLine) {
          nextActive = chapter.id;
        }
      });

      if (nextActive) {
        setActiveId((prev) => (prev === nextActive ? prev : nextActive));
      }
    };

    const schedule = () => {
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [chapters, orientation]);

  // Horizontal variant: as `activeId` changes, smoothly scroll the active tab
  // into the centre of the nav (or jump straight to it under reduced motion).
  React.useEffect(() => {
    if (orientation !== "horizontal") return;
    if (!activeId) return;
    const nav = navRef.current;
    const item = itemRefs.current[activeId];
    if (!nav || !item) return;

    const navRect = nav.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const target =
      nav.scrollLeft +
      (itemRect.left - navRect.left) -
      (navRect.width - itemRect.width) / 2;

    nav.scrollTo({
      left: Math.max(0, target),
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [activeId, orientation]);

  const handleClick = (id: string) => (e: React.MouseEvent) => {
    // Let modified clicks (cmd/ctrl/shift/alt + click) and middle-clicks
    // fall through to the browser so "open in new tab/window" works; only
    // intercept the plain left-click so we can smooth-scroll instead of
    // the native jump.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top =
      el.getBoundingClientRect().top + window.scrollY - getHeaderOffset() + 1;
    window.scrollTo({
      top,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
    // Keep the URL hash in sync so back/forward + copy-link target the
    // right chapter. `replaceState` avoids polluting history.
    if (typeof window !== "undefined" && window.history.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  const isHorizontal = orientation === "horizontal";

  return (
    <nav
      ref={navRef}
      aria-label="Page chapters"
      className={cn(
        "w-full",
        isHorizontal
          ? cn(
              "flex flex-row overflow-x-auto",
              "border-t border-[#313335]",
              "snap-x snap-mandatory",
              "[-ms-overflow-style:none] [scrollbar-width:none]",
              "[&::-webkit-scrollbar]:hidden"
            )
          : "flex flex-col",
        className
      )}
    >
      {chapters.map((chapter) => {
        const isActive = activeId === chapter.id;
        return (
          <a
            key={chapter.id}
            ref={(el) => {
              itemRefs.current[chapter.id] = el;
            }}
            href={`#${chapter.id}`}
            onClick={handleClick(chapter.id)}
            aria-current={isActive ? "true" : undefined}
            className={cn(
              "relative flex h-[72px] items-center justify-center text-[14px] leading-[1.4] font-medium tracking-[-0.02em] transition-colors duration-200 motion-reduce:transition-none",
              "border-b border-[#313335]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-inset",
              isHorizontal && "w-[148px] shrink-0 snap-start px-3",
              !isHorizontal && "px-3",
              isActive ? "text-white" : "text-white/70 hover:text-white"
            )}
          >
            <span className="relative z-[1] whitespace-nowrap">
              {chapter.label}
            </span>
            <span
              aria-hidden
              ref={(el) => {
                fillRefs.current[chapter.id] = el;
              }}
              className={cn(
                "pointer-events-none absolute inset-x-0 bottom-0 h-[1px] origin-left bg-white",
                isActive ? "opacity-100" : "opacity-0"
              )}
              style={{ transform: "scaleX(0)", willChange: "transform" }}
            />
          </a>
        );
      })}
    </nav>
  );
}
